import React, { useState, useEffect, useMemo, useContext } from 'react';

// MUI
import Paper from '@material-ui/core/Paper';
import Card from '@material-ui/core/Card';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import CardActionArea from '@material-ui/core/CardActionArea';
import { withStyles } from '@material-ui/core/styles';

// styles
import '../styles/About.css';

// Actions
import { getVersion, setMailer, getLogoLink, getHelpCatalog } from '../../Actions/';

// fuzzy search over the help catalog
import Fuse from 'fuse.js';

// Images
import labelsImage from '../../images/labels.png';

// Context
import { AppContext } from '../../Context/AppContextProvide';

const ImportExport = () => {
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [version, setVersion] = useState({});
  const [fName, setFName] = useState('');
  const [lName, setLName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [link, setLink] = useState();
  const to = 'support@showroompricing.com';
  const drawerWidth = 240;
  const styles = {
    tooltip: {
      maxWidth: '700px',
      height: '600px',
      borderRadius: '18px',
      boxShadow: '0 20px 80px 0',
      backgroundColor: '#28C4FC',
    },
  };
  const { user } = useContext(AppContext);
  const isAdmin = user.access_type_cd === 'a' ? true : false;
  const CustomTooltip = withStyles(styles)(Tooltip);

  useEffect(() => {
    let abortController = new AbortController();
    // Load the help catalog from our own server, which returns only approved+active
    // videos with absolute URLs (the server knows the catalog channel via config).
    getHelpCatalog()
      .then((r) => setVideos(r.data?.videos || []))
      .catch((err) => console.log('help catalog load failed', err));

    setEmail(JSON.parse(localStorage.getItem('userDetails')).email);
    getVersion()
      .then((result) => {
        setVersion(result.data);
      })
      .catch((err) => {
        console.log(err);
      });

    getLogoLink()
      .then((result) => {
        setLink(result.data.data[0].logo_link);
      })
      .catch((err) => {
        console.log(err);
      });
    return () => {
      abortController.abort();
    };
  }, [drawerWidth]);

  // The server returns an absolute thumbnail URL (or null → placeholder).
  const thumbFor = (v) => {
    if (v.thumbnail) return v.thumbnail;
    return undefined; // → placeholder in the card
  };

  // Client-side fuzzy search across the catalog.
  const results = useMemo(() => {
    if (!query.trim()) return videos;
    const fuse = new Fuse(videos, {
      keys: ['title', 'description', 'category', 'keywords'],
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
    return fuse.search(query).map((r) => r.item);
  }, [videos, query]);

  const YoutubeEmbed = ({ embedId }) => (
    <div className="video-responsive">
      <iframe
        width="100%"
        height="100%"
        src={`https://www.youtube.com/embed/${embedId}`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Tutorial"
      />
    </div>
  );

  const resetVars = () => {
    setFName('');
    setLName('');
    setEmail('');
    setPhone('');
    setMessageBody('');
  };

  const handleOpen = (video) => {
    setSelected(video);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAlertOpen = () => {
    setAlertOpen(true);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleFname = (e) => {
    setFName(e);
  };

  const handleLname = (e) => {
    setLName(e);
  };

  const handleEmail = (e) => {
    setEmail(e);
  };

  const handlePhone = (e) => {
    setPhone(e);
  };

  const handleMessageBody = (e) => {
    setMessageBody(e);
  };

  const handleSubmit = () => {
    setShowProgress(true);
    setMailer(
      to,
      fName,
      lName,
      email,
      phone,
      'Support request',
      messageBody,
      link
    )
      .then((result) => {
        resetVars();
        handleAlertOpen(true);
        setShowProgress(false);
      })
      .then((err) => {
        console.log(err);
      });
  };

  const alert = () => {
    return (
      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Message Sent!</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Thank you! We will get back to you soon.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleAlertClose}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const VideoCardData = ({ video }) => {
    const [imgError, setImgError] = useState(false);
    const thumb = thumbFor(video);
    const thumbStyle = { width: 160, height: 90, flexShrink: 0, objectFit: 'cover' };
    return (
      <Card elevation={2} className="help-list-card">
        <CardActionArea
          onClick={() => handleOpen(video)}
          style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start' }}
        >
          {thumb && !imgError ? (
            <CardMedia
              component="img"
              title={video.title}
              image={thumb}
              onError={() => setImgError(true)}
              style={thumbStyle}
            />
          ) : (
            <div
              style={{
                ...thumbStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#eceff1',
              }}
            >
              <OndemandVideoIcon style={{ fontSize: 40, color: '#90a4ae' }} />
            </div>
          )}
          <CardContent style={{ flex: 1, paddingTop: 8, paddingBottom: 8 }}>
            <Typography variant="subtitle1" component="div" style={{ fontWeight: 600 }}>
              {video.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {video.description}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderContactForm = () => {
    return (
      <div>
        {showProgress && <CircularProgress size={50} />}
        <h1>Send us a support request.</h1>

        <TextField
          margin="dense"
          id="fName"
          label="First Name"
          type="text"
          variant="filled"
          onChange={(e) => handleFname(e.target.value)}
          value={fName}
          className="text-field"
        />

        <TextField
          margin="dense"
          id="lName"
          label="Last Name"
          type="text"
          variant="filled"
          onChange={(e) => handleLname(e.target.value)}
          value={lName}
          className="text-field"
        />

        <TextField
          margin="dense"
          id="name"
          label="Email Address"
          type="email"
          variant="filled"
          required
          onChange={(e) => handleEmail(e.target.value)}
          value={email}
          className="text-field"
        />

        <TextField
          margin="dense"
          id="phone"
          label="Phone Number"
          type="number"
          variant="filled"
          onChange={(e) => handlePhone(e.target.value)}
          value={phone}
          className="text-field"
        />

        <TextField
          className="message-text-field"
          margin="dense"
          id="body"
          label="message"
          variant="filled"
          multiline
          rows={10}
          onChange={(e) => handleMessageBody(e.target.value)}
          value={messageBody}
        />
        <Button
          className="contact-button"
          onClick={handleSubmit}
          variant={'contained'}
        >
          Submit
        </Button>
      </div>
    );
  };

  const renderChangeNotes = () => {
    return (
      <div style={{ width: 600 }}>
        <Typography variant="button">
          <h2>CHANGELOG</h2>

          <h3>New Features you will find in the release:</h3>

          <ul>
            <li>
              View by day metrics now customizable by role, user type, and
              material class.
            </li>
            <li>Ability to show manufacturer on product page.</li>
            <li>Retail price override by product on product search page.</li>
            <li>Abiltiy to add customer sales tax.</li>
            <li>More detailed descriptions on QR codes.</li>
            <li>Show last modified date on product search page.</li>
            <li>
              Ability to add and show products with multiple distributors.
            </li>
            <li>
              Added additional search fields and an advanced seach section.
            </li>
            <li>Added additional search fields to manual upload template.</li>
            <li>Ability to add product disclaimner based on material class.</li>
            <li>View price by role on product page.</li>
            <li>Added locations to QR codes.</li>
            <li>Show last date user logged in.</li>
            <li>Added the ability to export csv files on file export page.</li>
          </ul>

          <h3>Changes you will find in the release:</h3>

          <ul>
            <li>New look with better contrast and uniform layouts.</li>
          </ul>
        </Typography>
      </div>
    );
  };

  return (
    <div className="container">
      <Paper className="about-tab-paper" id="about-tab-paper">
        <div className="about-tab-container">
          <div className="tab-links">
            <a href="#video-container" className="tab-links">
              <p className="tab-links">INSTRUCTIONAL VIDEOS</p>
            </a>
          </div>
          {isAdmin && (
            <div className="tab-links">
              <a href="#contact" className="tab-links">
                <p className="tab-links">TECHNICAL SUPPORT</p>
              </a>
            </div>
          )}
          <div className="tab-links">
            <a href="#labels" className="tab-links">
              <p className="tab-links">ORDER LABELS</p>
            </a>
          </div>
        </div>

        {/* Mobile Tabs */}

        <div className="about-tab-container-mobile">
          <div className="tab-links">
            <a href="#about" className="tab-links">
              <p className="tab-links">ABOUT</p>
            </a>
          </div>
          {isAdmin && (
            <div className="tab-links">
              <a href="#contact" className="tab-links">
                <p className="tab-links">CONTACT</p>
              </a>
            </div>
          )}
          <div className="tab-links">
            <a href="#video-container" className="tab-links">
              <p className="tab-links">INSTRUCTIONAL VIDEOS</p>
            </a>
          </div>
          <div className="tab-links">
            <a href="#labels" className="tab-links">
              <p className="tab-links">ORDER LABELS</p>
            </a>
          </div>
        </div>
      </Paper>
      {/* <div className="about-container"><h1 style={{marginTop:'5%'}}>Instructional Videos</h1></div> */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="lg"
        className="video-popup"
      >
        <DialogTitle className="video-dialog" disableTypography>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant={'h5'}>{selected ? selected.title : ''}</Typography>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {selected && selected.host === 's3' ? (
            <div className="video-responsive">
              <video controls autoPlay width="100%" src={selected.video_url} />
            </div>
          ) : (
            selected && <YoutubeEmbed embedId={selected.video_id} />
          )}
        </DialogContent>
      </Dialog>
      <div className="video-container">
        <Typography variant="h4" className="about__header">
          Instructional Videos
        </Typography>
        <Paper className="video-grid-paper" elevation={3}>
          <Grid container spacing={2} className="video-grid">
            <Grid item xs={12}>
              {/* <Typography
                  variant="h4"
                  className="about__header"
                >
                  Instructional Videos
                </Typography> */}
            </Grid>
            <Grid item xs={12}>
              <p style={{ fontSize: '25px' }}>
                Here you will find a number of helpful videos to guide you
                through the software. You can also visit our <a href="http://www.youtube.com/@showroompricing1866" target="_blank">youtube page</a> to see more.
              </p>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search help videos…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="help-search"
              />
            </Grid>
            {results.map((video) => (
              <Grid item xs={12} key={video.key}>
                <VideoCardData video={video} />
              </Grid>
            ))}
            {results.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body1" style={{ padding: '12px' }}>
                  No videos match “{query}”.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </div>
      <Divider />
      <div className="about-container">
        {isAdmin && (
          <div>
            <h1 style={{ marginTop: '5%' }}>Technical Support</h1>
            <p className="about__support-text">
              For support or any other questions please contact us at{' '}
              <a href="tel:314-530-9996" className="links">
                314-530-9996{' '}
              </a>
              or use the contact form below.
            </p>
            <br />
            <p className="support-hours">(Mon-Fri 9AM-5PM CST)</p>
            <br />
            <br id="contact" />
            <Paper className="contact-form" elevation={5}>
              <div>
                <Typography variant="h4" className="about__constact-header">
                  Contact Us
                </Typography>
              </div>
              {renderContactForm()}
            </Paper>
          </div>
        )}
        {/* <Button
          className="contact-button"
          variant="contained"
          onClick={handleContactFormOpen}
        >
          Contact Us
        </Button> */}
        <a href="#about-tab-paper" className="to-top-links">
          <p className="about__back-to-top-footer">BACK TO TOP</p>
        </a>
        <br />
        <Divider />
      </div>
      <div className="about-container">
        <h1 style={{ marginTop: '5%' }}>
          Click the link below to purchase labels
        </h1>
        <a
          id="labels"
          target="blank"
          href="https://www.onlinelabels.com/uses/showroompricing?reward=FRL2lTUaMKk%3D"
        >
          <img
            style={{ width: '75%', border: 'solid', borderColor: '#4E564E' }}
            src={labelsImage}
            alt={'Label link'}
          />
        </a>
      </div>
      <Divider />
      {/* <CustomTooltip title={renderChangeNotes()}> */}
      <div className="about-container">
        <Typography variant="h4" className="about__version">
          Versions
        </Typography>
        <h5>API Version: {version.version}</h5>
        <h5>APP Version: {version.app_version}</h5>
        <h5>DB Version: {version.db}</h5>
      </div>
      {/* </CustomTooltip> */}
      <Divider />
      {alert()}
    </div>
  );
};

export default ImportExport;
